/**
 * Decode HTML entities for DISPLAY in chat email result cards.
 *
 * Gmail returns subject/snippet HTML-entity-encoded (`O&#39;Brien`, `A&amp;B`).
 * React's JSX `{value}` escapes for XSS but does NOT decode pre-existing
 * entities, so the literal `&#39;` would render in the card. Decode here, at the
 * display boundary only — do not mutate stored data.
 *
 * This mirrors `decodeHtmlEntities` in `modules/email/emailLinkify.tsx`; it is
 * duplicated (not imported) because `features/` may not import from `modules/`
 * (one-way import rule, ADR 024).
 */

const NAMED_ENTITIES: Record<string, string> = {
  amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ',
  mdash: '—', ndash: '–', hellip: '…', rsquo: '’', lsquo: '‘',
  ldquo: '“', rdquo: '”', copy: '©', reg: '®', trade: '™',
};

/** Decode HTML entities (named + numeric, decimal and hex) in a text string. */
export function decodeHtmlEntities(input: string): string {
  if (!input || input.indexOf('&') === -1) return input;
  return input.replace(/&(#x?[0-9a-fA-F]+|[a-zA-Z][a-zA-Z0-9]*);/g, (whole, body: string) => {
    if (body[0] === '#') {
      const isHex = body[1] === 'x' || body[1] === 'X';
      const code = parseInt(body.slice(isHex ? 2 : 1), isHex ? 16 : 10);
      if (Number.isFinite(code) && code > 0) {
        try { return String.fromCodePoint(code); } catch { return whole; }
      }
      return whole;
    }
    const named = NAMED_ENTITIES[body] ?? NAMED_ENTITIES[body.toLowerCase()];
    return named ?? whole;
  });
}
